// Name and description of a catalogue entry in the active language: official content is translated
// (English is the source, French comes from the dictionaries); players' creations are shown as their author wrote them.
import type { AnyEntry } from '@gruvetto/creations';
import type { CosmeticSlot, CreationKind } from '@gruvetto/contracts';
import { t } from '../../i18n/index.js';
import { icon } from '../brand.js';
import { officialCosmeticOf } from '@gruvetto/creations';
import { modeName, modeDesc, trackName, trackDesc, workoutName, workoutDesc, planName, planDesc, moodName, lookName, officialName, officialDesc } from '../../i18n/content.js';

export function entryName(e: AnyEntry): string {
  const c = e.creation;
  if (e.source !== 'builtin') return c.name;
  switch (c.kind) {
    case 'mode': return modeName(e.id);
    case 'track': return trackName(e.id);
    case 'workout': return workoutName({ id: e.id, name: c.name });
    case 'plan': return planName(e.id);
    case 'theme': return moodName(e.id, c.name);
    case 'game': return officialName(e.id, c.name);
    case 'cosmetic': {
      const o = officialCosmeticOf(e.id);
      return o && o.slot !== 'wheels' ? lookName(o.slot === 'jersey' ? 'pattern' : o.slot, o.id) : officialName(e.id, c.name);
    }
    default: return c.name;
  }
}

export function entryDesc(e: AnyEntry): string {
  const c = e.creation;
  if (e.source !== 'builtin') return c.desc;
  switch (c.kind) {
    case 'mode': return modeDesc(e.id) || c.desc;
    case 'track': return trackDesc(e.id) || c.desc;
    case 'workout': return workoutDesc({ id: e.id, desc: c.desc });
    case 'plan': return planDesc(e.id) || c.desc;
    case 'theme': case 'game': return officialDesc(e.id, c.desc);
    default: return c.desc;
  }
}

/** Interface labels per type and per outfit slot. */
export const kindLabel = (k: CreationKind): string => t.creations.kinds[k].label;
export const kindPlural = (k: CreationKind): string => t.creations.kinds[k].plural;
export const slotName = (s: CosmeticSlot): string => t.creations.slots[s];
/** Line icon of each type (the catalogue emojis stay reserved for game content). */
export const KIND_ICON: Record<CreationKind, string> = { workout: 'activity', plan: 'layers', track: 'route', mode: 'flag', item: 'gift', theme: 'mountain', cosmetic: 'shirt', game: 'gamepad' };
export const kindIcon = (k: CreationKind, size = 15) => icon(KIND_ICON[k], size);
const VERB_ICON: Record<CreationKind, string> = { workout: 'play', track: 'play', mode: 'play', game: 'play', plan: 'play', cosmetic: 'shirt', item: 'gift', theme: 'mountain' };
/** Main button of a creation: icon and verb. */
export const verbHtml = (k: CreationKind) => `${icon(VERB_ICON[k], 15)}<span>${t.creations.verbs[k]}</span>`;
/** Label of a creation's main button, the same everywhere. */
export const verb = (k: CreationKind): string => t.creations.verbs[k];
